/**
 * PDF Password Detection Utility
 * 
 * Uses pdf.js to detect password-protected PDFs and validate passwords
 * client-side before uploading to the backend.
 */

import * as pdfjsLib from 'pdfjs-dist';

// Set the worker source for pdf.js
// Using unpkg CDN which mirrors npm directly (more reliable for matching versions)
const pdfVersion = pdfjsLib.version;
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfVersion}/build/pdf.worker.min.mjs`;

/**
 * Result of checking if a PDF needs a password
 */
export interface PdfPasswordCheckResult {
  needsPassword: boolean;
  error?: string;
}

/**
 * Result of validating a password for a PDF
 */
export interface PdfPasswordValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Helper function to check if an error is password-related
 * Handles different pdf.js versions and error structures
 */
function isPasswordError(error: any): boolean {
  // Check error name (various formats across pdf.js versions)
  const errorName = error?.name?.toLowerCase() || '';
  if (
    errorName === 'passwordexception' ||
    errorName.includes('password')
  ) {
    return true;
  }

  // Check error code (pdf.js uses numeric codes: 1 = NEED_PASSWORD, 2 = INCORRECT_PASSWORD)
  // In pdf.js 5.x, the error may have a 'code' property
  if (error?.code === 1 || error?.code === 2) {
    return true;
  }

  // Check error message for password-related keywords
  const errorMessage = (error?.message || error?.toString() || '').toLowerCase();
  const passwordIndicators = [
    'password',
    'encrypted',
    'need a password',
    'incorrect password',
    'no password given',
    'require password',
    'password required',
    'decryption',
    'permission denied'
  ];
  
  if (passwordIndicators.some(indicator => errorMessage.includes(indicator))) {
    return true;
  }

  return false;
}

/**
 * Check if a PDF file requires a user password to open.
 * 
 * Only flags PDFs that have a USER password (blocks opening entirely).
 * PDFs with only an OWNER password (restricts printing/copying) are NOT flagged
 * because backend Poppler tools (pdftotext, pdftoppm) ignore owner passwords
 * and extract text just fine.
 * 
 * Flow:
 * 1. Try opening the PDF without a password
 * 2. If it opens → try actual text extraction to confirm it works
 * 3. If text extraction fails due to owner-password permissions → still return needsPassword: false
 *    (backend handles it)
 * 4. If the PDF fails to open entirely → check if it's a password error → needsPassword: true
 * 
 * @param file - The PDF file to check
 * @returns Promise with needsPassword boolean
 */
export async function checkPdfPassword(file: File): Promise<PdfPasswordCheckResult> {
  // Only check PDF files
  if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
    return { needsPassword: false };
  }

  console.log(`[PDF Password Check] Checking file: ${file.name}`);

  try {
    const arrayBuffer = await file.arrayBuffer();
    
    const loadingTask = pdfjsLib.getDocument({
      data: arrayBuffer,
      // Don't provide a password - we want to detect if one is needed
    });

    try {
      const pdf = await loadingTask.promise;
      
      // 🎯 PDF opened successfully WITHOUT a password — this means it does NOT have
      // a "user password" (the kind that blocks opening). It may have an "owner password"
      // (restricts printing/copying), but the backend Poppler tools (pdftotext, pdftoppm)
      // ignore owner passwords entirely and extract text just fine.
      //
      // IMPORTANT: Always try actual text extraction FIRST before checking permissions.
      // Permission flags can be misleading — many PDF generators set encryption metadata
      // without actually blocking text extraction (e.g. AIG, Discovery, Sanlam policies).
      
      // Step 1: Try actual text extraction — this is the definitive test
        try {
          const page = await pdf.getPage(1);
          const textContent = await page.getTextContent();
        const itemCount = textContent.items?.length || 0;
        
        console.log(`[PDF Password Check] ${file.name} - Text extraction verified (${itemCount} items)`);
        
        // Text extraction works in the browser → backend will handle it too
          pdf.destroy();
          return { needsPassword: false };
        } catch (textError: any) {
        console.log(`[PDF Password Check] ${file.name} - Text extraction failed in browser:`, textError?.message);
        
        // Text extraction failed — check if it's a permission error
        const textErrMsg = (textError?.message || '').toLowerCase();
        if (textErrMsg.includes('permission') || textErrMsg.includes('extract')) {
          // pdf.js blocked extraction due to owner password permissions.
          // BUT: backend Poppler tools ignore owner passwords and CAN extract text.
          // So we should NOT prompt for a password — let the backend handle it.
          console.log(`[PDF Password Check] ${file.name} - Owner password restricts browser extraction, but backend Poppler tools will handle it`);
          pdf.destroy();
          return { needsPassword: false };
        }
        
        // Other text extraction error (rendering issue, etc.) — not a password problem
          pdf.destroy();
          return { needsPassword: false };
      }
      
    } catch (error: any) {
      console.log(`[PDF Password Check] ${file.name} - Error loading PDF:`, {
        name: error?.name,
        code: error?.code,
        message: error?.message
      });

      // Check if this is a password-related error
      if (isPasswordError(error)) {
        console.log(`[PDF Password Check] ${file.name} - Password REQUIRED (detected from error)`);
        return { needsPassword: true };
      }

      // Other error - PDF might be corrupted
      console.warn(`[PDF Password Check] ${file.name} - Non-password error:`, error);
      return { needsPassword: false, error: error.message };
    }
  } catch (error: any) {
    console.error(`[PDF Password Check] ${file.name} - Failed to read file:`, error);
    
    // Even outer errors could be password-related in some pdf.js versions
    if (isPasswordError(error)) {
      console.log(`[PDF Password Check] ${file.name} - Password REQUIRED (detected from outer error)`);
      return { needsPassword: true };
    }
    
    return { needsPassword: false, error: error.message };
  }
}

/**
 * Validate a password for a PDF file
 * 
 * @param file - The PDF fsile to validate
 * @param password - The password to try
 * @returns Promise with valid boolean
 */
export async function validatePdfPassword(
  file: File,
  password: string
): Promise<PdfPasswordValidationResult> {
  if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
    return { valid: false, error: 'Not a PDF file' };
  }

  console.log(`[PDF Password Validate] Validating password for: ${file.name}`);

  try {
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({
      data: arrayBuffer,
      password: password,
    });

    try {
      const pdf = await loadingTask.promise;
      // If we get here, the password worked
      console.log(`[PDF Password Validate] ${file.name} - Password VALID`);
      pdf.destroy();
      return { valid: true };
    } catch (error: any) {
      console.log(`[PDF Password Validate] ${file.name} - Error with password:`, {
        name: error?.name,
        code: error?.code,
        message: error?.message
      });

      // Check if this is a password-related error (wrong password)
      if (isPasswordError(error)) {
        return { valid: false, error: 'Incorrect password' };
      }

      // Other error
      return { valid: false, error: error.message };
    }
  } catch (error: any) {
    console.error(`[PDF Password Validate] ${file.name} - Failed to validate:`, error);
    
    // Password error at outer level
    if (isPasswordError(error)) {
      return { valid: false, error: 'Incorrect password' };
    }
    
    return { valid: false, error: error.message };
  }
}

/**
 * Check multiple PDF files for password protection
 * 
 * @param files - Array of files to check
 * @returns Promise with array of files that need passwords
 */
export async function checkMultiplePdfsForPassword(
  files: File[]
): Promise<{ file: File; needsPassword: boolean }[]> {
  const results = await Promise.all(
    files.map(async (file) => {
      const result = await checkPdfPassword(file);
      return { file, needsPassword: result.needsPassword };
    })
  );
  return results;
}
